import React, { useState, useEffect } from 'react';
import { Icons } from '../components/Icons';
import { db, appId } from '../utils/firebase';
import { collection, onSnapshot, deleteDoc, doc, updateDoc, addDoc, serverTimestamp } from 'firebase/firestore';
import AddDataModal from '../components/AddDataModal';

const CharacterBibleView = ({ isAdmin, showToast }) => {
    const [characters, setCharacters] = useState([]);
    const [isAdding, setIsAdding] = useState(false);
    const [editingChar, setEditingChar] = useState(null);
    const [selectedChar, setSelectedChar] = useState(null);

    useEffect(() => {
        if (!appId) return;
        const unsub = onSnapshot(collection(db, 'artifacts', appId, 'public', 'data', 'characters'), (snap) => {
            const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            data.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
            setCharacters(data);
            // BUG FIXED: Removed the aggressive "auto-select first character" logic here.
        });
        return () => unsub();
    }, []); // BUG FIXED: Removed selectedChar from dependency array so it doesn't loop.

    const handleDelete = async (id) => {
        if (!window.confirm("Purge this character profile?")) return;
        try {
            await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'characters', id));
            showToast("Profile purged.", "success");
            setSelectedChar(null);
        } catch(e) {
            showToast("Permission denied.", "error");
        }
    };

    const handleSaveChar = async (formData) => {
        try {
            const coll = collection(db, 'artifacts', appId, 'public', 'data', 'characters');
            if (editingChar) {
                await updateDoc(doc(coll, editingChar.id), formData);
                showToast("Profile updated.", "success");
            } else {
                await addDoc(coll, { ...formData, timestamp: serverTimestamp() });
                showToast("Profile logged.", "success");
            }
            setIsAdding(false);
            setEditingChar(null);
        } catch (e) { 
            console.error(e);
            showToast("Firebase Error: Could not save.", "error"); 
        }
    };

    const getFactionColor = (faction) => {
        if (faction === 'TAG') return 'text-red-500';
        if (faction === 'CRI') return 'text-green-500';
        return 'text-gray-400';
    };

    return (
        <div className="absolute inset-0 flex bg-[#050505] overflow-hidden fade-in">
            {/* Left Pane: "File Cabinet" List */}
            <div className={`w-full md:w-80 border-r border-gray-900 bg-black flex flex-col shrink-0 transition-all duration-300 ${selectedChar ? 'hidden md:flex' : 'flex'}`}>
                <div className="p-6 border-b border-gray-900 shrink-0">
                     <h2 className="text-xl font-bold flex items-center gap-2 text-white font-sans tracking-tight">
                        <Icons.User className="text-indigo-500" /> CHARACTER BIBLE
                    </h2>
                </div>
                
                <div className="flex-1 overflow-y-auto custom-scrollbar">
                    {characters.map(char => (
                        <div 
                            key={char.id} 
                            onClick={() => setSelectedChar(char)}
                            className={`flex items-center gap-4 px-4 py-3 border-b border-gray-900 cursor-pointer transition-colors ${selectedChar?.id === char.id ? 'bg-indigo-900/20' : 'hover:bg-gray-900'}`}
                        >
                            {char.imageUrl ? (
                                <img src={char.imageUrl} alt={char.name} className="w-10 h-10 rounded-full object-cover border-2 border-gray-800"/>
                            ) : (
                                <div className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center text-gray-500"><Icons.User size={20}/></div>
                            )}
                            <div>
                                <h3 className={`font-bold ${selectedChar?.id === char.id ? 'text-indigo-400' : 'text-white'}`}>{char.name}</h3>
                                <p className={`text-xs ${getFactionColor(char.faction)} font-mono`}>{char.role || 'Operative'}</p>
                            </div>
                        </div>
                    ))}
                </div>

                {isAdmin && (
                    <div className="p-4 border-t border-gray-900 shrink-0">
                         <button onClick={() => { setEditingChar(null); setIsAdding(true); }} className="w-full bg-indigo-900/20 text-indigo-400 border border-indigo-900/50 hover:bg-indigo-900 hover:text-white px-4 py-3 text-xs font-bold uppercase tracking-widest transition-colors flex items-center justify-center gap-2 rounded">
                            <Icons.Plus size={14} /> ADD NEW PROFILE
                        </button>
                    </div>
                )}
            </div>

            {/* Right Pane: "Open Dossier" View */}
            <div className={`flex-1 overflow-y-auto custom-scrollbar ${selectedChar ? 'block' : 'hidden md:block'}`}>
                {selectedChar ? (
                    <div className="flex flex-col h-full">
                        {/* MOBILE STICKY TOP NAVIGATION BAR */}
                        <div className="md:hidden sticky top-0 z-50 bg-black border-b border-gray-900 p-4 flex items-center justify-between shadow-lg">
                            <button onClick={() => setSelectedChar(null)} className="flex items-center gap-2 text-indigo-400 font-bold uppercase tracking-widest text-xs hover:text-white transition-colors">
                                <Icons.ArrowLeft size={16} /> BACK TO ROSTER
                            </button>
                            {isAdmin && (
                                <div className="flex gap-2">
                                    <button onClick={() => { setEditingChar(selectedChar); setIsAdding(true); }} className="text-gray-400 hover:text-white"><Icons.Edit2 size={16} /></button>
                                    <button onClick={() => handleDelete(selectedChar.id)} className="text-gray-400 hover:text-red-500"><Icons.Trash2 size={16} /></button>
                                </div>
                            )}
                        </div>

                        {/* DESKTOP ADMIN HEADER */}
                        <div className="hidden md:flex p-6 border-b border-gray-900 shrink-0 items-center justify-end">
                             {isAdmin && (
                                <div className="flex gap-2">
                                    <button onClick={() => { setEditingChar(selectedChar); setIsAdding(true); }} className="p-2 text-gray-400 hover:text-white transition-colors"><Icons.Edit2 size={18} /></button>
                                    <button onClick={() => handleDelete(selectedChar.id)} className="p-2 text-gray-400 hover:text-red-500 transition-colors"><Icons.Trash2 size={18} /></button>
                                </div>
                             )}
                        </div>

                        <div className="p-6 md:p-10 flex-1 pb-24">
                            <div className="flex flex-col md:flex-row items-center gap-8 mb-10 text-center md:text-left">
                                <div className="w-32 h-32 md:w-48 md:h-48 rounded-full bg-gray-900 shrink-0 border-4 border-gray-800 shadow-2xl overflow-hidden">
                                    {selectedChar.imageUrl ? (
                                        <img src={selectedChar.imageUrl} alt={selectedChar.name} className="w-full h-full object-cover" />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center text-gray-700"><Icons.User size={64}/></div>
                                    )}
                                </div>
                                <div>
                                    <span className={`text-sm font-bold uppercase tracking-[0.2em] mb-2 inline-block px-3 py-1 border rounded ${getFactionColor(selectedChar.faction)} border-current bg-current/10`}>
                                        {selectedChar.faction || 'UNAFFILIATED'}
                                    </span>
                                    <h1 className="text-4xl md:text-5xl font-black text-white uppercase tracking-tighter leading-none">{selectedChar.name}</h1>
                                    <h2 className="text-lg text-indigo-400 font-mono mt-2">{selectedChar.role}</h2>
                                </div>
                            </div>
                            
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
                                <div className="lg:col-span-2">
                                    <h4 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-4 border-b border-gray-800 pb-2">Biographical Profile</h4>
                                    <p className="text-gray-300 font-sans leading-relaxed whitespace-pre-wrap">{selectedChar.bio}</p>
                                </div>
                                <div>
                                    <h4 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-4 border-b border-gray-800 pb-2 flex items-center gap-2"><Icons.Briefcase size={14}/> Known Inventory</h4>
                                    <p className="text-gray-400 font-mono text-sm leading-relaxed whitespace-pre-wrap">{selectedChar.inventory}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="flex items-center justify-center h-full text-center p-10">
                        <div className="text-gray-700">
                            <Icons.User size={64} className="mx-auto mb-4"/>
                            <h3 className="font-bold text-lg text-gray-500 uppercase tracking-widest">Dossier Required</h3>
                            <p className="text-xs">Select a profile from the archive.</p>
                        </div>
                    </div>
                )}
            </div>

            {(isAdding || editingChar) && (
                <AddDataModal 
                    type="character_profile" 
                    initialData={editingChar || {}} 
                    onClose={() => { setIsAdding(false); setEditingChar(null); }} 
                    onSubmit={handleSaveChar} 
                    showToast={showToast} 
                />
            )}
        </div>
    );
};

export default CharacterBibleView;